import React, { useEffect } from "react"
import { Provider, useSelector } from "react-redux"
import { GatsbyBrowser } from "gatsby"
import { extractLangCode } from "./src/utils/general_utils"

import "@eventbrite/webapp-fonts/fonts.css"
// redux
import { RootState, store } from "./src/store/store"

// contentful live preview
import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react"

// @ts-ignore
import * as buttonStyles from "@eventbrite/marmalade/lib/components/Button/Button.css"
// @ts-ignore
import * as checkboxStyles from "@eventbrite/marmalade/lib/components/Checkbox/Checkbox.css"
// @ts-ignore
import * as choiceCardStyles from "@eventbrite/marmalade/lib/components/ChoiceCard/ChoiceCard.css"
// @ts-ignore
import * as choiceListStyles from "@eventbrite/marmalade/lib/components/ChoiceList/ChoiceList.css"
// @ts-ignore
import * as inlineAlertStyles from "@eventbrite/marmalade/lib/components/InlineAlert/InlineAlert.css"
// @ts-ignore
import * as radioStyles from "@eventbrite/marmalade/lib/components/Radio/Radio.css"
// @ts-ignore
import * as textAreaStyles from "@eventbrite/marmalade/lib/components/TextArea/TextArea.css"
// @ts-ignore
import * as textFieldStyles from "@eventbrite/marmalade/lib/components/TextField/TextField.css"
// @ts-ignore
import * as selectFieldStyles from "@eventbrite/marmalade/lib/components/SelectField/SelectField.css"
// @ts-ignore
import * as formControlStyles from "@eventbrite/marmalade/lib/components/FormControl/FormControl.css"
// @ts-ignore
import * as iconStyles from "@eventbrite/marmalade/lib/components/Icon/Icon.css"
// @ts-ignore
import * as stackStyles from "@eventbrite/marmalade/lib/components/Layout/Stack/Stack.css"
// @ts-ignore
import * as typographyStyles from "@eventbrite/marmalade/lib/components/Typography/Typography.css"
// @ts-ignore
import * as expansionPanelStyles from "@eventbrite/marmalade/lib/components/ExpansionPanel/ExpansionPanel.css"
// @ts-ignore
import * as tabsStyles from "@eventbrite/marmalade/lib/components/Tabs/Tabs.css"

import {
  StatsigProvider,
  useClientAsyncInit,
  StatsigUser
} from "@statsig/react-bindings"
import { userProfileGuardCheck } from "./src/store/slices/user_profile.slice"

const PENDING_USER: StatsigUser = {}

const StatsigProviderWrapper: React.FC<{
  children: React.ReactNode
  statsigUser: StatsigUser | null
}> = ({ children, statsigUser }) => {
  const { client } = useClientAsyncInit(
    process.env.GATSBY_STATSIG_CLIENT_API_KEY || "",
    statsigUser ?? PENDING_USER,
    { environment: { tier: process.env.NODE_ENV } }
  )

  const lastSentRef = React.useRef<string | null>(null)

  // Push the user to Statsig when it changes after initialization. This is also
  // what promotes the placeholder to a real user once auth resolves, and an
  // anonymous visitor to an identified one once they log in.
  useEffect(() => {
    if (!client || !statsigUser) return

    const serializedUser = JSON.stringify(statsigUser)

    if (lastSentRef.current !== serializedUser) {
      client.updateUserAsync(statsigUser)

      const reason =
        lastSentRef.current === null ? "initial update" : "value changed"
      lastSentRef.current = serializedUser

      if (process.env.GATSBY_DEBUG === "true") {
        console.log("[Statsig] user data updated", { statsigUser, reason })
      }
    }
  }, [client, statsigUser])

  return <StatsigProvider client={client}>{children}</StatsigProvider>
}

const StatsigWrapper: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const rawUserProfile = useSelector((state: RootState) => state.userProfile)
  const userProfile = userProfileGuardCheck(rawUserProfile)

  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  const userId = userProfile?.userData?.id
  const creatorTier = userProfile?.creatorTier
  const isAttendee = userProfile?.isAttendee
  const isSalesCreator = userProfile?.isSalesCreator ?? false

  const isLoggedInUserReady =
    Boolean(userId) && creatorTier !== undefined && isAttendee !== undefined

  const isAnonymousUserReady = hasAttemptedLogin && !rawUserProfile

  const lastResolvedUserRef = React.useRef<StatsigUser | null>(null)

  const statsigUser = React.useMemo<StatsigUser | null>(() => {
    let resolved: StatsigUser | null = null

    if (isLoggedInUserReady) {
      resolved = {
        userID: userId,
        custom: { creatorTier, isAttendee, isSalesCreator }
      }
    } else if (isAnonymousUserReady) {
      resolved = { custom: { isAnonymous: true } }
    }

    if (resolved) {
      lastResolvedUserRef.current = resolved
    }

    return resolved ?? lastResolvedUserRef.current
  }, [
    isLoggedInUserReady,
    isAnonymousUserReady,
    userId,
    creatorTier,
    isAttendee,
    isSalesCreator
  ])

  if (!statsigUser && process.env.GATSBY_DEBUG === "true") {
    console.log("[Statsig] auth not resolved — using placeholder user", {
      hasUserId: Boolean(userId),
      hasCreatorTier: creatorTier !== undefined,
      hasIsAttendee: isAttendee !== undefined,
      hasAttemptedLogin,
      isLoggedOut: !rawUserProfile
    })
  }

  return (
    <StatsigProviderWrapper statsigUser={statsigUser}>
      {children}
    </StatsigProviderWrapper>
  )
}

const LivePreviewWrapper: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const isPreview = process.env.GATSBY_CONTENTFUL_LIVE_PREVIEW === "true"

  if (!isPreview) {
    return <>{children}</>
  }

  const rawLocale = extractLangCode() || "en-us"
  const locale = rawLocale.replace(
    /-(\w+)/,
    (_, code: string) => `-${code.toUpperCase()}`
  )

  return (
    <ContentfulLivePreviewProvider
      locale={locale}
      enableInspectorMode
      enableLiveUpdates={false}
      debugMode={process.env.GATSBY_DEBUG === "true"}
    >
      {children}
    </ContentfulLivePreviewProvider>
  )
}

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({
  element
}) => {
  return (
    <Provider
      store={store}
      // @ts-ignore
      styles={{
        choiceCardStyles,
        buttonStyles,
        checkboxStyles,
        inlineAlertStyles,
        choiceListStyles,
        textAreaStyles,
        textFieldStyles,
        radioStyles,
        formControlStyles,
        selectFieldStyles,
        iconStyles,
        stackStyles,
        typographyStyles,
        expansionPanelStyles,
        tabsStyles
      }}
    >
      <LivePreviewWrapper>
        <StatsigWrapper>{element}</StatsigWrapper>
      </LivePreviewWrapper>
    </Provider>
  )
}

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location }) => {
  if (typeof window !== "undefined") {
    if (location?.pathname && window?.duet?.addMetadata) {
      window.duet.addMetadata({
        updated_url: location.pathname
      })
    }
  }
}

export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] =
  () => {
    window.CXoneDfo = "cxone"
  }
